import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import translations from 'client/internationalization/translation-map.json';
import { INode, IObject } from 'common/interfaces/hierarchy-entities';
import { findNameClash } from 'common/utils/name-clash';
import { moveObjectToNode as moveObjectToNodeAction } from 'client/store/entities/object/object';
import { MoveFileDialog } from './move-file-dialog';
import { ROOT_FOLDER_ID } from './move-targets';

type MoveStep = 'classification' | 'nameClash';

type PendingMove = {
  object: IObject;
  targetNodeId: string;
  targetName: string;
  targetClassification?: string;
  hasNameClash: boolean;
  step: MoveStep;
};

type UseMoveFileOptions = {
  nodes: INode[];
  objects: IObject[];
  rootParentNode?: Pick<INode, 'nodeid' | 'name'> & Partial<Pick<INode, 'classificationlevel'>>;
  moveObjectToNode: (...args: Parameters<typeof moveObjectToNodeAction>) => void;
};

const toNamedItem = (object: IObject) => {
  return { id: object.objectid, name: object.name };
};

export const useMoveFile = ({
  nodes, objects, rootParentNode, moveObjectToNode,
}: UseMoveFileOptions) => {
  const { t } = useTranslation();
  const [pendingMove, setPendingMove] = useState<PendingMove>();

  const close = () => {
    setPendingMove(undefined);
  };

  const execute = (move: PendingMove, keepBoth?: boolean) => {
    const { object, targetNodeId, targetName } = move;
    moveObjectToNode({
      objectid: object.objectid,
      nodeid: targetNodeId,
      oldNodeid: object.nodeid,
      keepBoth,
      successMessage: t(translations.fileMoved, { name: object.name, catalogue: targetName }) as string,
    });
    close();
  };

  const proceedAfterClassification = (move: PendingMove) => {
    if (move.hasNameClash) {
      return setPendingMove({ ...move, step: 'nameClash' });
    }
    execute(move);
  };

  const requestMove = (objectId: string, targetFolderId: string | number) => {
    const object = objects.find((entry) => entry.objectid === objectId);
    const targetNode = targetFolderId === ROOT_FOLDER_ID
      ? rootParentNode
      : nodes.find((node) => node.nodeid === String(targetFolderId));
    if (!object || !targetNode || targetNode.nodeid === object.nodeid) {
      return;
    }
    const targetNodeId = targetNode.nodeid;

    const objectsInTarget = objects.filter((entry) => entry.nodeid === targetNodeId);
    const move: PendingMove = {
      object,
      targetNodeId,
      targetName: targetNode.name,
      targetClassification: targetNode.classificationlevel,
      hasNameClash: !!findNameClash(toNamedItem(object), objectsInTarget.map(toNamedItem)),
      step: 'classification',
    };
    const hasDifferentClassification = !!targetNode.classificationlevel && targetNode.classificationlevel !== object.classificationlevel;
    if (hasDifferentClassification) {
      return setPendingMove(move);
    }
    proceedAfterClassification(move);
  };

  const renderClassificationDialog = (move: PendingMove) => {
    const { object, targetName, targetClassification } = move;
    const level = t(translations[`classificationLevel_${targetClassification}` as keyof typeof translations]);
    return (
      <MoveFileDialog
        heading={t(translations.moveClassificationHeading)}
        message={t(translations.moveClassificationMessage, { name: object.name, catalogue: targetName, level })}
        actions={[
          { label: t(translations.move), colored: true, onClick: () => proceedAfterClassification(move) },
          { label: t(translations.cancel), onClick: close },
        ]}
        onClose={close}
      />
    );
  };

  const renderNameClashDialog = (move: PendingMove) => {
    const { object, targetName } = move;
    return (
      <MoveFileDialog
        heading={t(translations.moveNameClashHeading)}
        message={t(translations.moveNameClashMessage, { name: object.name, catalogue: targetName })}
        actions={[
          { label: t(translations.keepBoth), colored: true, onClick: () => execute(move, true) },
          { label: t(translations.cancel), onClick: close },
        ]}
        onClose={close}
      />
    );
  };

  const renderDialog = () => {
    if (!pendingMove) {
      return null;
    }
    return pendingMove.step === 'classification'
      ? renderClassificationDialog(pendingMove)
      : renderNameClashDialog(pendingMove);
  };

  return { requestMove, renderDialog };
};
